const ATTR_REGULAR = {
    fill: '#47b2db',
    stroke: '#999',
    strokeWidth: 1,
    strokeLinejoin: 'round'
};

const ATTR_TAKEN = {
    fill: '#967b7b',
    stroke: '#333',
    strokeWidth: 1,
    strokeLinejoin: 'round'
};

const ATTR_MINE = {
    fill: '#FFCC00',
    stroke: '#FFCC00',
    strokeWidth: 1,
    strokeLinejoin: 'round'
};

const ATTR_STAGE = {
    fill: '#db4747',
    stroke: '#333',
    strokeWidth: 1,
    strokeLinejoin: 'round'
};

const ATTR_PRE = {
    fill: 'white',
    stroke: '#333',
    strokeWidth: 1,
    strokeLinejoin: 'round'
};

const getBoundingBox = (pathData) => {
    const numbers = pathData.match(/-?\d+\.?\d*/g).map(Number);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for(let i = 0; i < numbers.length; i += 2) {
        const x = numbers[i];
        const y = numbers[i + 1];
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
    }

    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
    };
};

const createPathAttributes = (attrs, scale = null, x = 0, y = 0) => {
    const attributes = [
        `fill="${attrs.fill}"`,
        `stroke="${attrs.stroke}"`,
        `stroke-width="${attrs.strokeWidth}"`,
        `stroke-linejoin="${attrs.strokeLinejoin}"`
    ];

    if(scale) {
        const [scaleX, scaleY] = scale.split(',');
        attributes.push(`transform="translate(${x},${y}) scale(${scaleX},${scaleY})"`);
    }

    return attributes.join(' ');
};

// Bounding box of an area's path in final viewBox coordinates (its translate/scale applied).
export const getAreaBoundingBox = (area) => {
    if(!area?.path) return null;
    const box = getBoundingBox(area.path);
    if(!area.scale) return box;

    const [scaleX, scaleY] = String(area.scale).split(',').map(Number);
    const x1 = (area.x || 0) + box.x * scaleX;
    const y1 = (area.y || 0) + box.y * scaleY;
    const x2 = (area.x || 0) + (box.x + box.width) * scaleX;
    const y2 = (area.y || 0) + (box.y + box.height) * scaleY;

    return {
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1)
    };
};

const PIN_PATH = 'M12 2C8.13 2 5 5.13 5 8.5c0 5.25 7 13.5 7 13.5s7-8.25 7-13.5C19 5.13 15.87 2 12 2z';

// "You sit here" pin, tip anchored on the marker point. Sized against the viewBox height so it
// comes out ~26 screen px at SeatMapSheet's 260px display cap, whatever scale the map was drawn at.
const createMarkerElement = ({x, y}, height) => {
    const pinHeight = Math.max(24, Number(height) * 0.1);
    const scale = pinHeight / 24;
    return `  <g data-seat-pin="1" transform="translate(${x},${y})">
    <ellipse data-pin-halo="1" cx="0" cy="0" rx="${(pinHeight * 0.4).toFixed(1)}" ry="${(pinHeight * 0.14).toFixed(1)}"/>
    <g transform="scale(${scale.toFixed(3)}) translate(-12,-22)">
      <g data-pin-drop="1">
        <path d="${PIN_PATH}" fill="#E0384C" stroke="#fff" stroke-width="1.4" stroke-linejoin="round"/>
        <circle cx="12" cy="8.7" r="2.9" fill="#fff"/>
      </g>
    </g>
  </g>`;
};

const createTextElement = ({x, y, text, fontSize, fontFamily = 'Assistant'}) => `
    <text 
      x="${x}" 
      y="${y}"
      font-family="${fontFamily}"
      fill="#fff"
      text-anchor="middle"
      dominant-baseline="middle"
      font-size="${fontSize}px"
    >${text}</text>`;

export const generateSVG = (data) => {
    const width = data.width || '763';
    const height = data.height || '761';
    const parts = [];

    parts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">`);

    // Draw areas
    if(data.area) {
        Object.entries(data.area).forEach(([aid, area]) => {
            if(!area.path) return;

            const isSoldOut = area.count === 0;
            const attrs = area.mine ? ATTR_MINE : isSoldOut ? ATTR_TAKEN : ATTR_REGULAR;
            const attributes = createPathAttributes(attrs, area.scale, area.x, area.y);
            const marker = area.mine ? ' data-mine="1"' : '';

            parts.push(`  <path d="${area.path}" ${attributes} data-aid="${aid}"${marker}/>`);

            if(area.title) {
                const bbox = getBoundingBox(area.path);
                const textX = bbox.x + bbox.width / 2;
                const textY = bbox.y + bbox.height / 2;
                const fontSize = Math.min(30, Math.max(12, width * 0.045));

                parts.push(createTextElement({
                    x: textX,
                    y: textY,
                    text: area.title,
                    fontSize
                }));
            }
        });
    }

    // Draw pre-areas
    if(data['area-pre']) {
        Object.entries(data['area-pre']).forEach(([, value]) => {
            if(!value.path) return;

            const attributes = createPathAttributes(ATTR_PRE, value.scale, value.x, value.y);
            parts.push(`  <path d="${value.path}" ${attributes}/>`);
        });
    }

    // Draw stage
    if(data.stage?.path) {
        const attributes = createPathAttributes(
            ATTR_STAGE,
            data.stage.scale,
            data.stage.x,
            data.stage.y
        );
        parts.push(`  <path d="${data.stage.path}" ${attributes} data-stage="1"/>`);

        const stageBBox = getBoundingBox(data.stage.path);
        parts.push(createTextElement({
            x: stageBBox.x + stageBBox.width / 2,
            y: stageBBox.y + stageBBox.height / 2,
            text: '',
            fontSize: width * 0.045
        }));
    }

    // Drawn last so it rides above every block
    if(data.marker) {
        parts.push(createMarkerElement(data.marker, height));
    }

    parts.push('</svg>');
    return parts.join('\n');
};
